import React, { useState } from 'react';
import { X, DollarSign, Share2, Eye, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';

const PublishInfoModal = ({ isOpen, onClose, onConfirm, productTitle }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const brand = useBrand();

  // Check if referrals are enabled for this brand
  const referralsEnabled = brand?.features?.referrals?.enabled !== false;
  const brandName = brand?.name || 'the store';

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (dontShowAgain) {
      // Use brand-specific localStorage key
      const storageKey = `${(brand?.name || 'store').toLowerCase().replace(/\s+/g, '_')}_hide_publish_info`;
      localStorage.setItem(storageKey, 'true');
    }
    onConfirm();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 text-white/80 hover:text-white rounded-full hover:bg-white/10"
          >
            <X size={20} />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <DollarSign size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Earn Money on Sales!</h2>
              <p className="text-white/90 text-sm">Making your product public</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Commission Info */}
          <div className="space-y-3">
            {referralsEnabled && (
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                <span className="text-2xl">🎨</span>
                <div>
                  <p className="font-semibold text-slate-900">10% Creator Commission</p>
                  <p className="text-sm text-slate-600">You earn 10% on every sale of your products</p>
                </div>
              </div>
            )}

            {referralsEnabled && (
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                <span className="text-2xl">🔗</span>
                <div>
                  <p className="font-semibold text-slate-900">5% Referral Bonus</p>
                  <p className="text-sm text-slate-600">Share your link and earn 5% when friends buy</p>
                </div>
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="border-t border-slate-200 pt-4">
            <p className="text-sm font-medium text-slate-700 mb-2">Quick tips:</p>
            <ul className="text-sm text-slate-600 space-y-1">
              <li className="flex items-center gap-2">
                <Eye size={14} className="text-slate-400" />
                Public products appear in the store for everyone
              </li>
              <li className="flex items-center gap-2">
                <Share2 size={14} className="text-slate-400" />
                Share on social media to maximize earnings
              </li>
            </ul>
          </div>

          {/* FAQ Link */}
          <Link
            to="/faq"
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
            onClick={onClose}
          >
            <HelpCircle size={16} />
            Learn more about selling on {brandName}
          </Link>

          {/* Don't show again */}
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            Don't show this again
          </label>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
          >
            Make Public
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishInfoModal;
